import React from "react";
import { MetadataRenderer } from "./DisplayHelpers";
import { BaseTrailblazerNode } from "../data/TrailblazerNode";
import { RouteHopFactory } from "../data/TrailblazerRouteHop";
import { linkType, TrailblazerEdgeType, TrailblazerNodeType } from "../data/TrailblazerTypes";
import TrailblazerEdge from "../data/TrailblazerEdge";
import RouteMetadataService from "./RouteMetadataService";

/**
 * Generates edges between nodes at two different positions using the same logic as generateEdgesFromNodes.
 * @param {Array} currentNodes - Nodes at the current position
 * @param {Array} nextNodes - Nodes at the next position
 * @returns {Array} - Array of edges between the positions
 */
const generateEdgesBetweenPositions = (currentNodes, nextNodes) => {
    const edges = [];

    if (currentNodes.length === 1 && nextNodes.length > 1) {
        // Case 1: One-to-many (split)
        nextNodes.forEach((nextNode) => {
            const edge = new TrailblazerEdge(
                currentNodes[0].id,
                nextNode.id,
                TrailblazerEdgeType.DIRECT,
                ""
            );
            edges.push(edge);
        });
    } else if (currentNodes.length > 1 && nextNodes.length === 1) {
        // Case 2: Many-to-one (reconcile)
        currentNodes.forEach((currentNode) => {
            const edge = new TrailblazerEdge(
                currentNode.id,
                nextNodes[0].id,
                TrailblazerEdgeType.DIRECT,
                ""
            );
            edges.push(edge);
        });
    } else {
        // Case 3: One-to-one or many-to-many (parallel paths)
        const minLength = Math.min(currentNodes.length, nextNodes.length);
        for (let i = 0; i < minLength; i += 1) {
            const edge = new TrailblazerEdge(
                currentNodes[i].id,
                nextNodes[i].id,
                TrailblazerEdgeType.DIRECT,
                ""
            );
            edges.push(edge);
        }
    }

    return edges;
};

/**
 * Given a list of nodes, create the edges list based on the adjacent node positions.
 * @param nodes
 */
const generateEdgesFromNodes = (nodes) => {
    const edges = [];
    const positionGroups = {};

    // Group nodes by their positions, maintaining order within each group
    nodes.forEach((node) => {
        if (positionGroups[node.position] === undefined) {
            positionGroups[node.position] = [];
        }
        positionGroups[node.position].push(node);
    });

    // Get all positions and sort them numerically
    const sortedPositions = Object.keys(positionGroups)
        .map(Number)
        .sort((a, b) => a - b);

    // For each position, connect to the next position only if they are adjacent
    for (let idx = 0; idx < sortedPositions.length - 1; idx += 1) {
        const currentPosition = sortedPositions[idx];
        const nextPosition = sortedPositions[idx + 1];

        // Only create edges if the positions are adjacent (difference is exactly 1)
        if (nextPosition - currentPosition === 1) {
            const currentNodes = positionGroups[currentPosition];
            const nextNodes = positionGroups[nextPosition];

            edges.push(...generateEdgesBetweenPositions(currentNodes, nextNodes));
        }
    }
    return edges;
};


/**
 * Generates edges that maintain connectivity even when intermediate positions are filtered out.
 * @param {Array} filteredNodes - The nodes after filtering
 * @returns {Array} - Array of edges that maintain proper connectivity
 */
const generateEdgesWithBridging = (filteredNodes) => {
    if (filteredNodes.length <= 1) {
        return [];
    }

    // Create a map of filtered nodes by position
    const filteredPositionMap = new Map();
    filteredNodes.forEach((node) => {
        if (!filteredPositionMap.has(node.position)) {
            filteredPositionMap.set(node.position, []);
        }
        filteredPositionMap.set(node.position, [...filteredPositionMap.get(node.position), node]);
    });

    const filteredPositions = Array.from(filteredPositionMap.keys()).sort((a, b) => a - b);
    const edges = [];

    // For each filtered position, connect to the next filtered position
    for (let i = 0; i < filteredPositions.length - 1; i += 1) {
        const currentPosition = filteredPositions[i];
        const nextPosition = filteredPositions[i + 1];

        const currentNodes = filteredPositionMap.get(currentPosition);
        const nextNodes = filteredPositionMap.get(nextPosition);

        // Generate edges between current and next position nodes
        const positionEdges = generateEdgesBetweenPositions(currentNodes, nextNodes);
        edges.push(...positionEdges);
    }

    return edges;
};

/**
 * Given a Trailblazer route, produces all the nodes and edges of the graph.
 */
const generateNodesAndEdgesFromRoute = (route) => {
    const nodes = [];

    const routeHops = route.hops;

    routeHops.forEach((hop, index) => {
        const nodeData = RouteHopFactory.createHop(hop);

        const node =
            new BaseTrailblazerNode(`${hop.name}-${index.toString()}`, nodeData, hop.position);
        nodes.push(node);
    });

    const edges = generateEdgesFromNodes(nodes);
    return { nodes, edges };
};

/**
 * Given a list of routes, produces all the nodes and edges of the graph.
 */
const generateNodesAndEdgesFromRoutes = (routes) => {
    const nodes = [];

    routes.forEach((route) => {
        const graphDataFromRoute = generateNodesAndEdgesFromRoute(route);
        nodes.push(...graphDataFromRoute.nodes);
    });

    const edges = generateEdgesFromNodes(nodes);

    return {
        nodes,
        edges
    };
};

/**
 * Given a list of nodes, sort them by their position and return the resulting list.
 */
const sortNodesByPosition = nodes =>
    [...nodes].sort((a, b) => {
        const posA = a.position != null ? a.position : 0;
        const posB = b.position != null ? b.position : 0;
        return posA - posB;
    });

/**
 * Finds keys that exist in all map objects.
 */
export const findCommonKeys = (mapList) => {
    const firstMap = mapList[0] || {};
    const allKeys = Object.keys(firstMap);

    // filter to only keys that exist in all map objects
    return allKeys.filter(key =>
        mapList.every(metadata =>
            metadata && key in metadata));
};

/**
 * Gets the display name for a metadata key from the ROUTE_METADATA_DEFINITION_MAP.
 * Falls back to camelCase conversion if not found in the map.
 * @param {string} key - The metadata key
 * @returns {string} - The display name
 */
const getDisplayName = (key) => {
    const definition = RouteMetadataService.ROUTE_METADATA_DEFINITION_MAP[key];
    if (definition && definition.displayName) {
        return definition.displayName;
    }
    return key;
};

/**
 * Given a list of map of key-value pairs (metadata in Trailblazer output),
 * generate the table data, returning the keys that all the metadata
 * commonly share and the table items with only those key-value pairs.
 * Columns are sorted by priority from ROUTE_METADATA_DEFINITION_MAP.
 */
const generateTableDataForMapList = (mapList) => {
    if (!mapList || mapList.length === 0) {
        return { tableItems: [], columnDefinitions: [] };
    }

    const commonKeys = findCommonKeys(mapList);

    // Sort keys by priority from ROUTE_METADATA_DEFINITION_MAP
    const sortedKeys = [...commonKeys].sort((a, b) => {
        const defA = RouteMetadataService.ROUTE_METADATA_DEFINITION_MAP[a];
        const defB = RouteMetadataService.ROUTE_METADATA_DEFINITION_MAP[b];
        const priorityA = defA?.priority ?? 999; // Unmapped keys go to end
        const priorityB = defB?.priority ?? 999;
        return priorityA - priorityB;
    });

    const columnDefinitions = sortedKeys.map(key => ({
        id: key,
        header: getDisplayName(key),
        cell: item => <MetadataRenderer fieldName={key} value={item[key]}/>
    }));

    const tableItems = mapList.map((metadata, index) => {
        const parsedItem = { id: `item-${index}` };

        sortedKeys.forEach((key) => {
            parsedItem[key] = metadata[key] || "-";
        });

        return parsedItem;
    });

    return { tableItems, columnDefinitions };
};

/**
 * Helper function to check if a node's metadata matches the filter criteria.
 * @param {TrailblazerNode} node - The node to check
 * @param {string} metadataKey - The metadata key to check
 * @param {*} metadataValue - The value to match
 * @param {boolean} exactMatch - Whether to perform exact or partial match
 * @returns {boolean} True if the node matches the filter criteria
 */
function checkNodeMetadata(node, metadataKey, metadataValue, exactMatch) {
    // Check if node has data and metadata
    if (!node.data || !node.data.metadata) {
        return false;
    }

    const nodeMetadataValue = node.data.toDataObject()[metadataKey];

    // If metadata key doesn't exist, return false
    if (nodeMetadataValue === undefined) {
        return false;
    }

    if (exactMatch) {
        return nodeMetadataValue === metadataValue;
    }
    // Partial match - convert both to strings and check if one contains the other
    const nodeValueStr = String(nodeMetadataValue).replace(/\s/g, "").toLowerCase();
    const filterValueStr = String(metadataValue).replace(/\s/g, "").toLowerCase();
    return nodeValueStr.includes(filterValueStr);
}

/**
 * Creates a filter function that filters nodes by a specific metadata key-value pair.
 * @param {string} metadataKey - The metadata key to filter by
 * @param {*} metadataValue - The metadata value to match
 * @param {boolean} exactMatch - Whether to perform exact match or partial match (default: true)
 * @returns {Function} Filter function that can be used with array.filter() or TrailblazerGraphData.applyFilter()
 */
function createMetadataFilter(metadataKey, metadataValue, exactMatch = true) {
    if (metadataKey === linkType) {
        // eslint-disable-next-line no-param-reassign
        exactMatch = false;
    }
    return (node) => {
        // Handle CollapsedTrailblazerNode - check if any collapsed node matches
        if (node.type === TrailblazerNodeType.COLLAPSED) {
            return node.collapsedNodes.some(collapsedNode =>
                checkNodeMetadata(collapsedNode, metadataKey, metadataValue, exactMatch));
        }

        // Handle BaseTrailblazerNode
        if (node.type === TrailblazerNodeType.BASE) {
            return checkNodeMetadata(node, metadataKey, metadataValue, exactMatch);
        }

        return false;
    };
}

export {
    generateNodesAndEdgesFromRoute,
    generateNodesAndEdgesFromRoutes,
    generateEdgesBetweenPositions,
    generateEdgesFromNodes,
    generateEdgesWithBridging,
    sortNodesByPosition,
    generateTableDataForMapList,
    checkNodeMetadata,
    createMetadataFilter,
    getDisplayName
};